import { useEffect, useRef, useState } from "react"
import { Plus, Trash2 } from "lucide-react"
import { ROLLEN, nummerKurz, textZuZeilen, zeilenFehler, zeilenZuText, type Rolle, type TeamZeile } from "@/lib/teamRufnummern"

/** Tabelle statt Textfeld für `tenants.telefon_namen` (Paul 08.10.2026). */
export function TeamRufnummern({ wert, onChange, inputCls, mailNummern, onMailNummern }: {
  wert: string; onChange: (text: string) => void; inputCls: string
  /** Wer per WhatsApp Mails senden darf (`tenants.wa_mail_nummern`, letzte neun Ziffern — Paul 15.10.2026). */
  mailNummern?: string[]; onMailNummern?: (nummern: string[]) => void
}) {
  // Eigene Zeilen: sonst wuerde jedes Tippen durch Text → Zeilen laufen und ein
  // Leerzeichen am Ende („Max ") sofort verschlucken. Von aussen (Laden) neu
  // uebernehmen, wenn der Text nicht von uns kommt.
  const [zeilen, setZeilen] = useState<TeamZeile[]>(() => textZuZeilen(wert))
  const zuletzt = useRef(wert)
  useEffect(() => {
    if (wert !== zuletzt.current) { setZeilen(textZuZeilen(wert)); zuletzt.current = wert }
  }, [wert])
  const fehler = zeilenFehler(zeilen)
  const setze = (neu: TeamZeile[]) => {
    setZeilen(neu)
    const text = zeilenZuText(neu)
    zuletzt.current = text
    onChange(text)
  }
  const aendere = (i: number, teil: Partial<TeamZeile>) => setze(zeilen.map((z, j) => (j === i ? { ...z, ...teil } : z)))
  const mailAn = (kurz: string, an: boolean) => {
    if (!onMailNummern) return
    const rest = (mailNummern || []).filter((k) => k !== kurz)
    onMailNummern(an ? [...rest, kurz] : rest)
  }
  const neueZeile = () => setZeilen([...zeilen, { nummer: "", name: "", rolle: "" }])
  return (
    <div data-testid="team-rufnummern">
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--text-muted)", fontSize: 12 }}>
            <th style={{ padding: "4px 6px 4px 0", fontWeight: 500 }}>Name</th>
            <th style={{ padding: "4px 6px", fontWeight: 500 }}>Handy- oder Festnetznummer</th>
            <th style={{ padding: "4px 6px", fontWeight: 500 }}>Rolle</th>
            {onMailNummern && <th style={{ padding: "4px 6px", fontWeight: 500 }}
              title="Darf über den WhatsApp-Assistenten E-Mails an Kunden schicken">✉️ Mails per WhatsApp</th>}
            <th />
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z, i) => (
            <tr key={i} data-testid={`team-zeile-${i}`}>
              <td style={{ padding: "3px 6px 3px 0", verticalAlign: "top" }}>
                <input className={inputCls} value={z.name} placeholder="z. B. Arman"
                  onChange={(e) => aendere(i, { name: e.target.value })} data-testid={`team-name-${i}`} />
              </td>
              <td style={{ padding: "3px 6px", verticalAlign: "top" }}>
                <input className={inputCls} value={z.nummer} placeholder="0176 12345678" inputMode="tel"
                  onChange={(e) => aendere(i, { nummer: e.target.value })} data-testid={`team-nummer-${i}`} />
                {fehler[i] && <div style={{ color: "#b42318", fontSize: 11, marginTop: 2 }}
                  data-testid={`team-fehler-${i}`}>{fehler[i]}</div>}
              </td>
              <td style={{ padding: "3px 6px", verticalAlign: "top" }}>
                <select className={inputCls} value={z.rolle} onChange={(e) => aendere(i, { rolle: e.target.value as Rolle })}
                  data-testid={`team-rolle-${i}`}>
                  <option value="">—</option>
                  {ROLLEN.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </td>
              {onMailNummern && (
                <td style={{ padding: "3px 6px", verticalAlign: "middle", textAlign: "center" }}>
                  <input type="checkbox" disabled={!nummerKurz(z.nummer)}
                    checked={!!nummerKurz(z.nummer) && (mailNummern || []).includes(nummerKurz(z.nummer))}
                    onChange={(e) => mailAn(nummerKurz(z.nummer), e.target.checked)}
                    aria-label={`${z.name || "Diese Nummer"} darf per WhatsApp Mails senden`}
                    data-testid={`team-mail-${i}`} />
                </td>
              )}
              <td style={{ padding: "3px 0", verticalAlign: "top" }}>
                <button type="button" className="pp-btn pp-btn-sm" title="Entfernen"
                  onClick={() => setze(zeilen.filter((_, j) => j !== i))} data-testid={`team-weg-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="pp-btn pp-btn-sm" style={{ marginTop: 6 }} onClick={neueZeile}
        data-testid="team-neu">
        <Plus className="w-3.5 h-3.5" /> Person hinzufügen
      </button>
    </div>
  )
}
